// ─── fetchClientMealSummary.js ──────────────────────────────────────────
// Boucle de suivi (2026-07-25, cadrage Benoit) — synthèse 7 jours des repas
// (suivis / remplacés / sautés / jours sans info, détail par journée) et de
// l'hydratation (moyenne, évolution, jours sans donnée) d'une cliente,
// lue depuis l'app cliente via /api/admin/client-meal-summary.
//
// Des FAITS, jamais de note globale ni de score d'observance : le calcul
// vit côté app (lib/meal-log-summary.ts, testé), le cockpit ne fait
// qu'afficher. `water` arrive à null quand le repère est à 0 → la donnée
// reste locale à l'app, rien n'est montré à Anissa.

import { clientAppFetch } from "./clientAppFetch";
import { clientIdentityFields, hasClientIdentity } from "./clientIdentity";

/**
 * @param {object} client - ligne `clients` du SaaS
 * @param {number} days   - 1-30, défaut 7
 * @returns {Promise<{ ok: boolean, found?: boolean, meals?: object, water?: object|null, error?: string }>}
 */
export async function fetchClientMealSummary(client, days = 7) {
  if (!hasClientIdentity(client)) {
    return { ok: false, error: "Cliente sans email ni client_id" };
  }
  let body;
  try {
    body = await clientAppFetch("/api/admin/client-meal-summary", {
      method: "GET",
      query: { ...clientIdentityFields(client), days },
    });
  } catch (e) {
    return { ok: false, error: e?.message || "Erreur reseau" };
  }
  if (!body?.ok) {
    return { ok: false, error: body?.error || "Reponse invalide" };
  }
  return {
    ok: true,
    found: !!body.found,
    meals: body.meals || null,
    water: body.water ?? null,
  };
}

/** Créneaux repas en français (les logs stockent les codes de l'app). */
export const MEAL_SLOT_LABELS = {
  breakfast: "Petit-déjeuner",
  morning_snack: "Collation du matin",
  lunch: "Déjeuner",
  afternoon_snack: "Collation",
  dinner: "Dîner",
  evening_snack: "Collation du soir",
};

export function mealSlotLabel(slot) {
  return MEAL_SLOT_LABELS[slot] || slot || "Repas";
}

/** Libellé factuel d'un état de repas (jamais de jugement).
 *  Cadrage Benoit v2 : « comme prévu » (clair, non ambigu), « à venir »
 *  pour un repas futur d'aujourd'hui, « non renseigné » pour un repas
 *  passé sans saisie. */
export function mealStateLabel(status) {
  if (status === "done") return "comme prévu";
  if (status === "skip") return "sauté";
  if (status === "replaced") return "remplacé";
  if (status === "upcoming") return "à venir";
  if (status === "missing") return "non renseigné";
  return status || "";
}

/** Nombre en français (virgule décimale). */
export function frNumber(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return String(n).replace(".", ",");
}

/** Tendance hydratation (cadrage Benoit v2) : différence CHIFFRÉE plutôt
 *  qu'un « en baisse » trop affirmatif. { kind:'stable' } → « tendance
 *  stable » ; { kind:'diff', diff } → « −1,2 verre/jour par rapport aux
 *  7 jours précédents » ; null (données insuffisantes) → rien. */
export function formatWaterTrend(trend) {
  if (!trend) return null;
  if (trend.kind === "stable") return "tendance stable";
  if (trend.kind === "diff") {
    const sign = trend.diff > 0 ? "+" : "−";
    const abs = Math.abs(trend.diff);
    const unit = abs >= 2 ? "verres" : "verre";
    return `${sign}${frNumber(abs)} ${unit}/jour par rapport aux 7 jours précédents`;
  }
  return null;
}
